<template>
  <div>
    <b-row
      :class="`row-container tw-flex tw-flex-row ${
        isSecurityAndStabilityScore ? 'tw-gap-[10px]' : 'tw-gap-[40px]'
      } tw-mr-0 tw-ml-0`"
    >
      <b-col
        :class="[
          'tw-pr-0 tw-pl-0 tw-flex',
          type === INPUT_TYPE.TextArea ? 'tw-items-start' : 'tw-items-center',
          colLabelClass,
        ]"
      >
        <label
          :class="[
            'tw-mb-0 tw-text-[13px] tw-text-[#231F20] tw-leading-[145%] tw-font-medium tw-font-Poppins',
            classTitle,
          ]"
          >{{ title }}</label
        >
        <span v-if="required" class="tw-text-[red]">&nbsp;*</span>
        <slot name="rightHeader"></slot>
        <slot name="statusHeader"></slot>
      </b-col>

      <b-col :class="`col-sm-6 tw-pr-0 tw-pl-0 ${colClass}`">
        <DatePickInput
          v-if="type === INPUT_TYPE.Date"
          v-model="value"
          :disabled="disabled"
          :placeholder="placeholder"
          :is-auto-focus="isAutoFocus"
          :class-name="`${
            validation?.$errors[0] || validState !== null
              ? 'is-invalid-input'
              : ''
          } ${inputClass} ${isApiError ? '!tw-border-[red]' : ''}`"
          :valid-state="validState"
          @on:focus="handleFocus"
        />
        <BFormInput
          v-else-if="type !== INPUT_TYPE.TextArea"
          v-model="value"
          data-cy="wizard-input-question-input"
          :type="type"
          :disabled="disabled"
          :placeholder="placeholder"
          :class="[
            isApiError ? '!tw-border-[red]' : '',
            'input-wizard-question autoFocus',
            validation?.$error &&
            (!isFocus ||
              (isFocus && validation?.$errors?.[0]?.$validator === 'required'))
              ? 'is-invalid-input'
              : '',
          ]"
          @blur="handleBlur(value)"
          @focus="handleFocus"
          @input="updateStore"
        />
        <BFormTextarea
          v-else-if="type === INPUT_TYPE.TextArea"
          id="note"
          v-model="value"
          data-cy="wizard-input-question-input-area"
          :disabled="disabled"
          :class="`${
            isApiError ? '!tw-border-[red]' : ''
          } autoFocus tw-h-[90px] tw-flex tw-w-full tw-items-start tw-gap-2.5 tw-border tw-text-[#231F20] tw-text-[13px] tw-px-5 tw-pt-[17.5px] tw-pb-[17px] tw-rounded-md tw-border-solid tw-border-[#EFF0F1] tw-bg-[#eff0f180] ${
            validation?.$errors[0] ? 'is-invalid-input' : ''
          } ${isApiError ? '!tw-border-[red]' : ''}`"
          :placeholder="placeholder"
          :no-resize="true"
          @focus="handleFocus"
        />
        <slot name="rightInput"></slot>
      </b-col>
    </b-row>
    <b-row
      :class="`row-container tw-flex tw-flex-row ${
        isSecurityAndStabilityScore ? 'tw-gap-[10px]' : 'tw-gap-[40px]'
      } tw-mr-0 tw-ml-0`"
    >
      <b-col
        :class="`col-container tw-pr-0 tw-pl-0 tw-flex tw-items-center ${colLabelClass}`"
      />
      <b-col :class="`col-sm-6 tw-pr-0 tw-pl-0 ${colClass}`">
        <slot name="feedback">
          <b-form-invalid-feedback
            v-if="
              !isFocus ||
              (isFocus &&
                validation?.$errors?.[0]?.$validator === 'required') ||
              validState !== null
            "
            :class="`${
              validation?.$error || validState !== null ? 'is-invalid' : ''
            } ${validationClass}`"
          >
            <VRuntimeTemplate
              :template="validation?.$errors?.[0]?.$message || validMessage"
            />
          </b-form-invalid-feedback>
        </slot>
      </b-col>
    </b-row>
    <b-row
      class="row-container tw-flex tw-flex-row tw-gap-[40px] tw-mr-0 tw-ml-0"
    >
      <b-col :class="'col-container tw-pr-0 tw-pl-0 tw-flex tw-items-center'" />
      <b-col :class="'col-sm-6 tw-pr-0 tw-pl-0'">
        <slot />
      </b-col>
    </b-row>
  </div>
</template>
<script setup lang="ts">
import { INPUT_TYPE } from "@/utils/constants";
import { computed, ref } from "vue";
import DatePickInput from "../common/datePickInput.vue";
import VRuntimeTemplate from "vue3-runtime-template";
import { PropType } from "vue";

let timeoutId;

const props = defineProps({
  disabled: { type: Boolean, required: false },
  title: { type: String, required: true },
  index: { type: Number, required: false, default: 0 },
  type: { type: String, required: false, default: "text" },
  placeholder: { type: String, required: true },
  required: { type: Boolean, required: false, default: false },
  classTitle: { type: String, required: false, default: "" },
  colClass: { type: String, required: false, default: "" },
  colLabelClass: { type: String, required: false, default: "" },
  validState: {
    type: Boolean as PropType<boolean | null>,
    required: false,
    default: null,
  },
  modelValue: {
    type: [String, Number],
    default: () => {
      return null;
    },
  },
  validMessage: { type: String, required: false, default: "" },
  delayChangeInput: { type: Number, default: 0, required: false },
  validation: { type: Object, required: false, default: null },
  isAutoFocus: { type: Boolean, required: false, default: false },
  inputClass: { type: String, required: false, default: "" },
  validationClass: { type: String, required: false, default: "" },
  isApiError: {
    type: Boolean,
    required: false,
    default: false,
  },
  isSecurityAndStabilityScore: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const isFocus = ref(false);

const emit = defineEmits([
  "update:modelValue",
  "update:valueTeamPage",
  "handleBlur",
  "update:store",
  "on:focus",
]);

const updateStore = () => {
  emit("update:valueTeamPage", true);
};

const value: any = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    if (props.delayChangeInput > 0) updateWithDelayTimeOut(newValue);
    else {
      emit("update:modelValue", newValue);
      emit("update:store");
    }
  },
});

const updateWithDelayTimeOut = (value) => {
  timeoutId && clearTimeout(timeoutId);
  timeoutId = setTimeout(() => {
    emit("update:modelValue", value);
    emit("update:store");
  }, 650);
};

const handleBlur = (value) => {
  isFocus.value = false;
  props.validation?.$validate();
  emit("handleBlur", { value: value, index: props.index });
};

const handleFocus = () => {
  isFocus.value = true;
  emit("on:focus", true);
};
</script>
<style lang="scss" scoped>
input.input-wizard-question {
  border-radius: 6px;
  border: 1px solid var(--ui-rule, #eff0f1);
  background: var(--ui-rule-50, rgba(239, 240, 241, 0.5));
  padding: 18px 20px;
  height: 56px;
  color: #231f20;
  font-size: 13px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  &::placeholder {
    color: #b7b7b7;
  }
}

.invalid-feedback {
  font-size: 11px !important;
  font-family: Poppins !important;
  font-style: normal !important;
  font-weight: 500 !important;
  color: #f7553c !important;
  line-height: 130% !important;
  margin-top: 10px !important;
}

.input-wizard-question.is-invalid-input {
  border: 1px solid #f7553c;
  background: #fee5e2;

  &::after {
    content: "";
  }
}

textarea::placeholder {
  color: #b7b7b7;
  font-size: 13px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

@media screen and (max-width: 840px) {
  .row-container {
    flex-direction: column;
    gap: 20px;

    .col-sm-6 {
      max-width: 100%;
    }
  }
  .col-container {
    display: none;
  }
  .invalid-feedback {
    margin-left: 15px;
  }
}
@media screen and (max-width: 1070px) {
  .row-container {
    gap: 20px;

    .col-sm-6 {
      max-width: 60%;
    }
  }
}
</style>
